import { __ } from '@wordpress/i18n';
import { useCallback, useEffect, useMemo, useState } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { addQueryArgs } from '@wordpress/url';
import {
    AdminDataViews as DataViews,
    DokanTooltip as Tooltip,
    DokanModal,
    DokanLink,
} from '@dokan/components';
import { truncate } from '@dokan/utilities';
import { SimpleInput } from '@getdokan/dokan-ui';
import { Search } from 'lucide-react';
import StatusPill from '../components/quote-rule-list/StatusPill';
import HeaderSection from '../components/common/HeaderSection';

const TABS = [
    { name: 'all', label: __( 'All', 'dokan' ) },
    { name: 'published', label: __( 'Published', 'dokan' ) },
    { name: 'draft', label: __( 'Draft', 'dokan' ) },
    { name: 'trash', label: __( 'Trash', 'dokan' ) },
];

const QuoteRulesList = () => {
    const [ data, setData ] = useState< any[] >( [] );
    const [ isLoading, setIsLoading ] = useState( false );
    const defaultLayouts = useMemo(
        () => ( {
            table: { density: 'comfortable' },
            grid: {},
            list: {},
        } ),
        []
    );

    const [ selection, setSelection ] = useState< any[] >( [] );
    const [ totalItems, setTotalItems ] = useState( 0 );
    const [ activeTab, setActiveTab ] = useState( 'all' );
    const [ search, setSearch ] = useState( '' );
    const [ statusCounts, setStatusCounts ] = useState< {
        all: number;
        publish: number;
        draft: number;
        trash: number;
    } >( { all: 0, publish: 0, draft: 0, trash: 0 } );

    // Confirmation modal state
    const [ confirmModal, setConfirmModal ] = useState< {
        isOpen: boolean;
        title: string;
        description: string;
        onConfirm: () => void;
    } >( { isOpen: false, title: '', description: '', onConfirm: () => {} } );

    const fields = [
        {
            id: 'rules',
            label: __( 'Rules', 'dokan' ),
            render: ( { item }: any ) => {
                const ruleName =
                    item?.rule_name ||
                    item?.title ||
                    item?.name ||
                    __( '—', 'dokan' );
                const ruleId = item?.id || item?.rule_id;
                const isTruncated = ruleName.length > 50;
                return (
                    <div className="flex flex-col gap-1 min-w-48">
                        <DokanLink
                            as="a"
                            href={ `#/request-for-quote/edit-quote-rule/${ ruleId }` }
                            className="cursor-pointer"
                        >
                            { isTruncated ? (
                                <Tooltip content={ ruleName }>
                                    <span className="font-medium text-[#7047EB]">
                                        { truncate( ruleName, 50 ) }
                                    </span>
                                </Tooltip>
                            ) : (
                                <span className="font-medium text-[#7047EB]">
                                    { ruleName }
                                </span>
                            ) }
                        </DokanLink>
                        { ( item?.created_at || item?.date ) && (
                            <span className="text-xs text-gray-500">
                                { item?.created_at || item?.date }
                            </span>
                        ) }
                    </div>
                );
            },
        },
        {
            id: 'status',
            label: __( 'Status', 'dokan' ),
            render: ( { item }: any ) => {
                const raw =
                    item?.status ?? item?.post_status ?? item?.rule_status;
                const normalized = ( () => {
                    const str =
                        raw == null
                            ? ''
                            : String( raw ).trim().toLowerCase();
                    if ( ! str ) return 'draft';
                    if ( str === 'published' ) return 'publish';
                    return str;
                } )();
                return <StatusPill value={ normalized } />;
            },
        },
        {
            id: 'price_visibility',
            label: __( 'Price Visibility', 'dokan' ),
            render: ( { item }: any ) => {
                const hidden =
                    item?.hide_price === 'Yes' ||
                    item?.hide_price === 1 ||
                    item?.hide_price === true;
                return (
                    <div className="min-w-48">
                        <span>
                            { hidden
                                ? __( 'Hidden', 'dokan' )
                                : __( 'Visible', 'dokan' ) }
                        </span>
                    </div>
                );
            },
        },
        {
            id: 'priority',
            label: __( 'Priority', 'dokan' ),
            render: ( { item }: any ) => {
                return (
                    <div className="min-w-48">
                        { String( item?.rule_priority ?? item?.priority ?? 0 ) }
                    </div>
                );
            },
        },
    ];

    const filterFields = useMemo(
        () => [
            {
                id: 'search',
                label: __( 'Search', 'dokan' ),
                render: () => (
                    <SimpleInput
                        addOnLeft={ <Search size={ 16 } /> }
                        className="border rounded px-3 py-1.5 w-full bg-white"
                        input={ {
                            type: 'search',
                            value: search,
                            placeholder: __( 'Search rules…', 'dokan' ),
                            onChange: ( e: any ) => setSearch( e.target.value ),
                        } }
                    />
                ),
            },
        ],
        [ search ]
    );

    const tabs = useMemo(
        () =>
            TABS.map( ( t ) => {
                const countKey = t.name === 'published' ? 'publish' : t.name;
                const count = ( statusCounts as any )[ countKey ] || 0;
                return {
                    id: t.name,
                    name: t.name,
                    icon: (
                        <div className="flex items-center gap-1.5 px-2">
                            { t.label }
                            <span className="text-xs font-light text-[#A5A5AA]">
                                ({ count })
                            </span>
                        </div>
                    ),
                    title: t.label,
                };
            } ),
        [ statusCounts ]
    );

    const [ view, setView ] = useState( {
        perPage: 10,
        page: 1,
        search: '',
        type: 'table',
        status: 'all',
        titleField: 'rules',
        layout: { density: 'comfortable' },
        fields: fields.map( ( f ) =>
            [ 'status', 'price_visibility', 'priority' ].includes( f.id )
                ? f.id
                : null
        ),
    } );

    const fetchData = useCallback( async () => {
        setIsLoading( true );
        try {
            const apiStatus = activeTab === 'published' ? 'publish' : activeTab;
            const params: Record< string, any > = {
                page: view.page,
                per_page: view.perPage,
                status: apiStatus !== 'all' ? apiStatus : undefined,
                search: search || undefined,
            };
            const path = addQueryArgs(
                'dokan/v1/request-for-quote/quote-rule',
                params as any
            );
            const response: any = await apiFetch( {
                path,
                parse: false,
            } ).catch( () => null );
            if ( response ) {
                const items = await response.json().catch( () => [] );
                setData( Array.isArray( items ) ? items : [] );
                const headers = response.headers;

                // Extract status counts from response headers
                const publishCount = parseInt(
                    headers.get( 'X-Status-Publish' ) || '0',
                    10
                );
                const draftCount = parseInt(
                    headers.get( 'X-Status-Draft' ) || '0',
                    10
                );
                const trashCount = parseInt(
                    headers.get( 'X-Status-Trash' ) || '0',
                    10
                );

                // "All" = sum of all statuses except trash
                const allCount = publishCount + draftCount;

                const counts: Record< string, number > = {
                    all: allCount,
                    publish: publishCount,
                    draft: draftCount,
                    trash: trashCount,
                };
                setStatusCounts( counts as any );

                // Set totalItems based on the active tab's count for correct pagination
                const countKey =
                    activeTab === 'published' ? 'publish' : activeTab;
                setTotalItems( counts[ countKey ] || 0 );
            } else {
                setData( [] );
                setTotalItems( 0 );
                setStatusCounts( { all: 0, publish: 0, draft: 0, trash: 0 } );
            }
        } finally {
            setIsLoading( false );
        }
    }, [ view.page, view.perPage, activeTab, search ] );

    useEffect( () => {
        fetchData();
    }, [ fetchData ] );

    return (
        <div className="dokan-rfq-admin-quote-rules-list">
            <div className="mb-6 flex items-center justify-between">
                <h2 className="text-2xl font-semibold text-gray-900">
                    { __( 'Quote Rules List', 'dokan' ) }
                </h2>
            </div>

            <HeaderSection active="rules" />

            <DataViews
                data={ data }
                namespace="rfq-quote-rules-list"
                defaultLayouts={ {
                    table: {
                        perPage: 20,
                        fields: [
                            'quote',
                            'status',
                            'price_visibility',
                            'priority',
                        ],
                    },
                } }
                fields={ fields as any }
                getItemId={ ( item: any ) => item?.id || item?.rule_id }
                onChangeView={ setView as any }
                paginationInfo={ {
                    totalItems,
                    totalPages: Math.max(
                        1,
                        Math.ceil( totalItems / view.perPage )
                    ),
                } }
                view={ view as any }
                selection={ selection }
                onChangeSelection={ setSelection as any }
                isLoading={ isLoading }
                tabs={ {
                    tabs,
                    onSelect: ( tabName: string ) => {
                        setActiveTab( tabName );
                        setView( ( prev: any ) => ( { ...prev, page: 1 } ) );
                    },
                    initialTabName: activeTab,
                } }
                actions={ [
                    {
                        id: 'trash',
                        label: () => __( 'Trash', 'dokan' ),
                        supportsBulk: true,
                        isEligible: ( item: any ) =>
                            item.status === 'publish' ||
                            item.status === 'published' ||
                            item.status === 'draft',
                        callback: ( items: any[] ) => {
                            const ids = items.map( ( i ) => i.id || i.rule_id );
                            if ( ! ids.length ) return;
                            setConfirmModal( {
                                isOpen: true,
                                title: __(
                                    'Trash Confirmation',
                                    'dokan'
                                ),
                                description: __(
                                    'Move selected rules to Trash?',
                                    'dokan'
                                ),
                                onConfirm: async () => {
                                    await apiFetch( {
                                        path: 'dokan/v1/request-for-quote/quote-rule/batch',
                                        method: 'POST',
                                        data: { trash: ids },
                                    } ).catch( () => {} );
                                    setSelection( [] );
                                    fetchData();
                                },
                            } );
                        },
                    },
                    {
                        id: 'restore',
                        label: () => __( 'Restore', 'dokan' ),
                        supportsBulk: true,
                        isEligible: ( item: any ) => item.status === 'trash',
                        callback: async ( items: any[] ) => {
                            const ids = items.map( ( i ) => i.id || i.rule_id );
                            if ( ! ids.length ) return;
                            await apiFetch( {
                                path: 'dokan/v1/request-for-quote/quote-rule/batch',
                                method: 'POST',
                                data: { restore: ids },
                            } ).catch( () => {} );
                            setSelection( [] );
                            fetchData();
                        },
                    },
                    {
                        id: 'delete',
                        label: () => __( 'Delete Permanently', 'dokan' ),
                        supportsBulk: true,
                        isEligible: ( item: any ) => item.status === 'trash',
                        callback: ( items: any[] ) => {
                            const ids = items.map( ( i ) => i.id || i.rule_id );
                            if ( ! ids.length ) return;
                            setConfirmModal( {
                                isOpen: true,
                                title: __(
                                    'Delete Confirmation',
                                    'dokan'
                                ),
                                description: __(
                                    'Delete selected rules permanently?',
                                    'dokan'
                                ),
                                onConfirm: async () => {
                                    await apiFetch( {
                                        path: 'dokan/v1/request-for-quote/quote-rule/batch',
                                        method: 'POST',
                                        data: { delete: ids },
                                    } ).catch( () => {} );
                                    setSelection( [] );
                                    fetchData();
                                },
                            } );
                        },
                    },
                ] }
            />

            <DokanModal
                isOpen={ confirmModal.isOpen }
                namespace="rfq-quote-rules-confirm"
                onClose={ () =>
                    setConfirmModal( ( prev ) => ( {
                        ...prev,
                        isOpen: false,
                    } ) )
                }
                onConfirm={ confirmModal.onConfirm }
                dialogTitle={ confirmModal.title }
                confirmationTitle={ confirmModal.title }
                confirmationDescription={ confirmModal.description }
                confirmButtonText={ __( 'Confirm', 'dokan' ) }
                cancelButtonText={ __( 'Cancel', 'dokan' ) }
            />
        </div>
    );
};

export default QuoteRulesList;
